import { Component, ReactNode } from "react";
import { captureWithContext } from "@/lib/sentry";
import { hardReload } from "@/lib/hard-reload";

// A dynamically-imported chunk can fail to load — most often because a new
// build shipped while a client was still holding the old index.html, so the
// hashed chunk it asks for (e.g. CMCApp-<oldhash>.js) no longer exists. Since
// /assets/* is cached immutable, the cure is to re-fetch the NON-immutable
// index.html (which carries the new hashes) — i.e. a single reload self-heals.
// Bundlers word this failure differently, so match them all.
const CHUNK_ERROR =
  /dynamically imported module|Loading chunk|ChunkLoadError|Importing a module script failed|Failed to fetch dynamically/i;
const RELOADED_AT = "cmc_chunk_reload_at";

function isChunkError(err: Error | null): boolean {
  return !!err && CHUNK_ERROR.test(err.message || "");
}

/**
 * Catches render crashes anywhere in the route tree. For a stale-chunk load
 * failure it reloads ONCE (guarded against a reload loop) to pull the fresh
 * build; for anything else it shows a recoverable error card. Wraps the whole
 * router so lazy-loaded routes (CMCApp on /, /teacher, /demo/*) can never leave
 * the user on a blank screen.
 */
export class ChunkErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    if (isChunkError(error)) {
      let last = 0;
      try { last = Number(sessionStorage.getItem(RELOADED_AT) || 0); } catch { /* storage blocked */ }
      // Only auto-reload if we haven't just done so — otherwise a genuinely
      // missing chunk would loop forever; fall through to the card instead.
      if (Date.now() - last > 10_000) {
        try { sessionStorage.setItem(RELOADED_AT, String(Date.now())); } catch { /* ignore */ }
        void hardReload();
        return;
      }
    }
    captureWithContext(error, {
      componentStack: info.componentStack ?? "(none)",
      chunkError: isChunkError(error),
    });
  }

  render() {
    if (this.state.error) {
      const chunk = isChunkError(this.state.error);
      return (
        <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "sans-serif", background: "#f9fafb" }}>
          <div style={{ maxWidth: 420, textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{chunk ? "🔄" : "⚠️"}</div>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8, color: "#111" }}>
              {chunk ? "A newer version is available" : "Something went wrong"}
            </h2>
            <p style={{ color: "#555", fontSize: 14, marginBottom: 20 }}>
              {chunk ? "Reload to get the latest version of CoverMyClass." : this.state.error.message}
            </p>
            <button
              onClick={() => { try { sessionStorage.removeItem(RELOADED_AT); } catch { /* ignore */ } void hardReload(); }}
              style={{ background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
